import { useMutation, useQueryClient } from "@tanstack/react-query";
import { duplicateAnalysis, getGetAnalysisQueryKey, setAnalysisState } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  type AnalysisVersionInfo,
  AnalysisVersionInfoState,
  AnalysisWriteForbiddenErrorCode,
  SetAnalysisStateBodyState,
} from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../AnalysisDetailContext";
import { type SourceVersion, getDuplicateErrorResource } from "./utils";

type Variables = {
  readonly sourceVersion: SourceVersion;
  readonly latestVersion: AnalysisVersionInfo;
};

const Failure = {
  closeFailed: "closeFailed",
  closedButNotCreated: "closedButNotCreated",
  notCreated: "notCreated",
} as const;

type Failure = (typeof Failure)[keyof typeof Failure];

class CloseAndDuplicateError extends Error {
  constructor(
    readonly failure: Failure,
    readonly causeCode?: unknown,
  ) {
    super(failure);
  }
}

const getCauseCode = (error: unknown) => (error as { code?: unknown } | null)?.code;

// the analysis was handed over after the menu was opened, retrying cannot help until it is taken back
const isHandedOver = (error: CloseAndDuplicateError) =>
  error.failure === Failure.closeFailed && error.causeCode === AnalysisWriteForbiddenErrorCode.analysis_read_only_access;

/**
 * A new version of an own analysis whose latest version is in progress. The duplicate endpoint only adds a version on top
 * of a closed latest one, so the latest version is closed as declined first - that cannot be undone, so a failed copy
 * leaves it closed and the next attempt only copies.
 *
 * The errors carry the API code as `causeCode`, not `code`, so a 403 from closing does not open the global
 * `AnalysisWriteForbiddenModal` - the dialog explains it instead.
 */
export const useCloseAndDuplicate = () => {
  const { t } = useResource();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);

  const { mutate, isPending, error, variables, reset } = useMutation<
    Awaited<ReturnType<typeof duplicateAnalysis>>,
    CloseAndDuplicateError,
    Variables
  >({
    mutationFn: async ({ sourceVersion, latestVersion }) => {
      const isClosingLatest = latestVersion.state === AnalysisVersionInfoState.in_progress;
      if (isClosingLatest) {
        await setAnalysisState(analysisId, latestVersion.versionId, { state: SetAnalysisStateBodyState.declined }).catch((cause) => {
          throw new CloseAndDuplicateError(Failure.closeFailed, getCauseCode(cause));
        });
      }

      return duplicateAnalysis({ sourceAnalysisGuid: sourceVersion.guid }).catch((cause) => {
        throw new CloseAndDuplicateError(isClosingLatest ? Failure.closedButNotCreated : Failure.notCreated, getCauseCode(cause));
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: getGetAnalysisQueryKey(analysisId, versionId), refetchType: "none" });
      toast.success(t(Resources.AnalysisDetail.Toast.NewVersionCreated, { version: result.version }));
      navigate(`${AppRouting.AnalysisDetail.path(result.analysisId, result.versionId)}${location.search}`);
    },
    onError: (error, { latestVersion }) => {
      // awaited before the mutation settles, so a retry already sees the closed latest version and only copies
      if (error.failure === Failure.closedButNotCreated) {
        return queryClient.invalidateQueries({ queryKey: getGetAnalysisQueryKey(analysisId, versionId) });
      }
      // the menu item then turns disabled until the analysis is taken back
      if (isHandedOver(error)) {
        return queryClient.invalidateQueries({ queryKey: getGetAnalysisQueryKey(analysisId, latestVersion.versionId) });
      }
    },
  });

  const getErrorText = () => {
    if (!error) return null;

    switch (error.failure) {
      case Failure.closeFailed:
        return isHandedOver(error)
          ? t(Resources.AnalysisDetail.Duplicate.Error.HandedOver)
          : t(Resources.AnalysisDetail.Duplicate.ClosingLatest.Error.CloseFailed);
      case Failure.closedButNotCreated:
        return t(Resources.AnalysisDetail.Duplicate.ClosingLatest.Error.ClosedButNotCreated, {
          latestVersion: variables?.latestVersion.version,
        });
      case Failure.notCreated:
        return t(getDuplicateErrorResource(error.causeCode) ?? Resources.AnalysisDetail.Duplicate.ClosingLatest.Error.NotCreated);
    }
  };

  return {
    closeAndDuplicate: (variables: Variables, options?: { onSuccess?: () => void }) => mutate(variables, options),
    isPending,
    errorText: getErrorText(),
    reset,
  };
};
